import apiClient from './apiClient';
import { STORAGE_KEYS } from '../../constants';

// ── In-memory PII cache ─────────────────────────────────────────────
// Email & phone are PII — they must NOT be persisted to localStorage.
// Instead they live in this module-level variable, populated by
// storeSession() (which is already called after every /api/session or
// login response) and cleared by clearStoredSession().
// On a full page reload the cache is empty until the next getSession()
// call repopulates it, which already happens in HomeRoute/ProtectedRoute.
let _piiCache = { email: null, phone: null };

// ── In-flight deduplication for getSession ──────────────────────────
// Multiple components (App.js HomeRoute, ProtectedRoute, ChatPage,
// OrgChatPage) can call getSession() within milliseconds of each other
// on page load. Without this guard each caller fires its own network
// request. The guard collapses all concurrent cookie-based calls into a
// single in-flight promise so only one GET /api/session is made.
// Token-based calls (used in special auth flows) are not deduplicated
// because they carry distinct credentials and must always be fresh.
let _sessionPromise = null;

/**
 * Internal: unconditionally fetch a fresh session from the backend.
 * Backend determines agent based on JWT token (sent via httpOnly cookie).
 *
 * @param {string|null} token - Optional explicit Bearer token
 * @returns {Promise<Object>} Session data: { userId, username, groups, agent, expiresAt }
 */
async function _fetchSession(token) {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 10_000);
  try {
    const options = { signal: controller.signal };
    if (token) options.headers = { Authorization: `Bearer ${token}` };
    const response = await apiClient.get('/api/session', options);
    return response.data;
  } catch (error) {
    console.error('[SessionAPI] Error:', error.message);

    // AbortController timeout
    if (error.code === 'ERR_CANCELED' || error.name === 'AbortError' || error.name === 'CanceledError') {
      throw new Error('BACKEND_UNAVAILABLE');
    }

    // Handle specific HTTP errors
    if (error.response?.status === 401) {
      throw new Error('UNAUTHORIZED');
    }
    if (error.response?.status === 403) {
      const detail = error.response?.data?.detail;
      if (detail === 'ACCOUNT_DISABLED') {
        throw new Error('ACCOUNT_DISABLED');
      }
      // STAFF-DEVICE-LOCK / FE-2 — distinct device-refusal codes. Must NOT
      // fall through to the generic FORBIDDEN below: callers (LoginPage,
      // App.js, ProtectedRoute, OrgChatPage) key off these exact codes to
      // sign the user out (RC-2) instead of showing a generic access-denied.
      if (detail === 'DEVICE_NOT_APPROVED' || detail === 'DEVICE_ID_MISSING') {
        const deviceErr = new Error(detail);
        deviceErr.code = detail;
        throw deviceErr;
      }
      throw new Error('FORBIDDEN');
    }

    throw error;
  } finally {
    clearTimeout(timeoutId);
  }
}

/**
 * Get user session from backend.
 * Cookie-based calls (no token) are deduplicated: concurrent callers
 * share one in-flight request and all receive the same resolved value.
 *
 * @param {string|null} token - Optional explicit Bearer token (skips dedup)
 * @returns {Promise<Object>} Session data: { userId, username, groups, agent, expiresAt }
 */
export const getSession = async (token = null) => {
  // Token-based calls carry distinct credentials — always fetch fresh.
  if (token) return _fetchSession(token);

  // Cookie-based: collapse concurrent calls into one in-flight promise.
  if (_sessionPromise) return _sessionPromise;

  _sessionPromise = _fetchSession(null).finally(() => {
    _sessionPromise = null;
  });

  return _sessionPromise;
};

/**
 * Check if token is expired based on stored expiry time
 * Note: The actual JWT tokens are now in httpOnly cookies (not accessible via JS).
 * This only checks the metadata expiry timestamp stored in localStorage.
 * @returns {boolean} - True if token is expired
 */
export const isTokenExpired = () => {
  const expiry = localStorage.getItem(STORAGE_KEYS.TOKEN_EXPIRY);
  
  if (!expiry) {
    return true;
  }
  
  const expiryTime = parseInt(expiry) * 1000; // Convert to milliseconds
  const now = Date.now();
  
  return now >= expiryTime;
};

/**
 * Get stored session data from localStorage
 * Note: Only non-sensitive session metadata is stored here.
 * JWT tokens are in httpOnly cookies (managed by the browser, not accessible via JS).
 * @returns {Object|null} - Stored session data or null
 */
export const getStoredSession = () => {
  const agentType = localStorage.getItem(STORAGE_KEYS.AGENT_TYPE);
  const userId = localStorage.getItem(STORAGE_KEYS.USER_ID);
  const username = localStorage.getItem(STORAGE_KEYS.USERNAME);
  const displayName = localStorage.getItem(STORAGE_KEYS.DISPLAY_NAME);
  const tokenExpiry = localStorage.getItem(STORAGE_KEYS.TOKEN_EXPIRY);
  const onboardingCompleted = localStorage.getItem(STORAGE_KEYS.ONBOARDING_COMPLETED);
  const staffOnboarded = localStorage.getItem(STORAGE_KEYS.STAFF_ONBOARDED);
  const userType = localStorage.getItem(STORAGE_KEYS.USER_TYPE);
  const organizationId = localStorage.getItem(STORAGE_KEYS.ORGANIZATION_ID);
  const role = localStorage.getItem(STORAGE_KEYS.ROLE);
  const memberId = localStorage.getItem(STORAGE_KEYS.MEMBER_ID);
  
  if (!agentType || !userId) {
    return null;
  }
  
  return {
    agent: agentType,
    userId,
    username,
    displayName: displayName || null,
    email: _piiCache.email,
    phone: _piiCache.phone,
    expiresAt: tokenExpiry ? parseInt(tokenExpiry) : null,
    onboarding_completed: onboardingCompleted === 'true',
    staff_onboarded: staffOnboarded === 'true',
    userType: userType || 'individual',
    organizationId: organizationId || null,
    role: role || null,
    member_id: memberId || null,
  };
};

/**
 * Store session metadata in localStorage
 * IMPORTANT: JWT tokens are NO LONGER stored in localStorage.
 * They are delivered as httpOnly cookies by the backend and managed by the browser.
 * Only non-sensitive session metadata (agent type, user ID, expiry) is stored here.
 * 
 * @param {Object} sessionData - Session data from backend
 */
export const storeSession = (sessionData) => {
  // Handle both flat response and nested { data: { ... } } response shapes
  const data = sessionData?.data || sessionData;

  // ── F1 identity guard (pure equality) ────────────────────────────
  // If /api/session resolves a userId different from the one already in
  // localStorage, this is a silent identity swap (e.g. a future
  // server-side bug in the same class as the original cookie-jar
  // incident). Treat it as auth ambiguity: hard logout and redirect to
  // /login instead of adopting the new identity.
  //
  // This is now pure equality with no timing window — callers (LoginPage,
  // SignupPage) intentionally do NOT call storeSession on the login
  // response anymore. Only the canonical /api/session response writes
  // userId, so the two formats can never disagree by design.
  const prevUserId = localStorage.getItem(STORAGE_KEYS.USER_ID);
  if (data?.userId && prevUserId && prevUserId !== data.userId) {
    // Dedupe: if a previous F1 trigger is already in progress, no-op.
    // Without this, every storeSession call between F1 firing and the
    // browser actually navigating to /login keeps retriggering signOut +
    // POST /api/auth/logout. The pattern mirrors apiClient._handleAuthFailure.
    try {
      if (sessionStorage.getItem('__ak_auth_failed')) return;
      sessionStorage.setItem('__ak_auth_failed', '1');
    } catch { /* SSR / privacy mode */ }

    // eslint-disable-next-line no-console
    console.warn('[storeSession] silent identity swap detected — forcing relogin', {
      prevUserId,
      newUserId: data.userId,
    });

    // CRITICAL: clear localStorage synchronously BEFORE returning. Without
    // this, the caller (HomeRoute) continues with setIsAuthenticated(true),
    // mounts the protected route, which calls /api/session again, which
    // calls storeSession again — and prev is still the stale value because
    // signOut() hadn't yet cleared it. Result: a /api/session request loop
    // until the rate limiter kicks in at 120/min. Clearing here breaks the
    // loop because subsequent route guards see empty localStorage and take
    // their empty-state early-out to /login without hitting the backend.
    clearStoredSession();

    // Best-effort server-side cleanup in background — revoke the Cognito
    // refresh token + clear httpOnly cookies. We do NOT await it; the
    // user-visible redirect happens immediately via location.replace.
    import('../auth/authApi').then(({ signOut }) => {
      Promise.resolve(signOut()).catch(() => {});
    }).catch(() => {});

    // Redirect — browser navigates when the JS call stack drains.
    try { window.location.replace('/login'); } catch { /* SSR */ }
    return;  // Do NOT write the new identity into localStorage.
  }

  if (data?.userId) localStorage.setItem(STORAGE_KEYS.USER_ID, data.userId);
  if (data?.username) localStorage.setItem(STORAGE_KEYS.USERNAME, data.username);
  if (data?.agent) localStorage.setItem(STORAGE_KEYS.AGENT_TYPE, data.agent);
  if (data?.expiresAt) localStorage.setItem(STORAGE_KEYS.TOKEN_EXPIRY, String(data.expiresAt));
  
  // Store display name, email, phone if provided by session API
  if (data?.displayName) localStorage.setItem(STORAGE_KEYS.DISPLAY_NAME, data.displayName);
  else if (data?.display_name) localStorage.setItem(STORAGE_KEYS.DISPLAY_NAME, data.display_name);
  else if (data?.name) localStorage.setItem(STORAGE_KEYS.DISPLAY_NAME, data.name);

  // EMAIL and PHONE are PII — cached in memory only, never in localStorage.
  _piiCache.email = data?.email || _piiCache.email || null;
  _piiCache.phone = data?.phone || data?.phone_number || _piiCache.phone || null;

  const onboardingStatus = data?.onboarding_completed !== undefined ? data.onboarding_completed : false;
  localStorage.setItem(STORAGE_KEYS.ONBOARDING_COMPLETED, onboardingStatus === true ? 'true' : 'false');

  // staff_onboarded flag (for org users)
  if (data?.staff_onboarded !== undefined) {
    localStorage.setItem(STORAGE_KEYS.STAFF_ONBOARDED, data.staff_onboarded === true ? 'true' : 'false');
  }
  
  if (data?.userType) localStorage.setItem(STORAGE_KEYS.USER_TYPE, data.userType);
  if (data?.organizationId || data?.org_id) localStorage.setItem(STORAGE_KEYS.ORGANIZATION_ID, data.organizationId || data.org_id);
  if (data?.role) localStorage.setItem(STORAGE_KEYS.ROLE, data.role);
  if (data?.member_id) localStorage.setItem(STORAGE_KEYS.MEMBER_ID, data.member_id);

  // JWT tokens are NOT stored here — they arrive as httpOnly cookies from the backend
};

/**
 * Clear stored session data
 */
export const clearStoredSession = () => {
  // Clear in-memory PII cache
  _piiCache = { email: null, phone: null };

  localStorage.removeItem(STORAGE_KEYS.USER_ID);
  localStorage.removeItem(STORAGE_KEYS.USERNAME);
  localStorage.removeItem(STORAGE_KEYS.DISPLAY_NAME);
  // Also clean up any legacy PII that older code may have stored
  localStorage.removeItem('email');
  localStorage.removeItem('phone');
  localStorage.removeItem(STORAGE_KEYS.AGENT_TYPE);
  localStorage.removeItem(STORAGE_KEYS.TOKEN_EXPIRY);
  localStorage.removeItem(STORAGE_KEYS.ONBOARDING_COMPLETED);
  localStorage.removeItem(STORAGE_KEYS.STAFF_ONBOARDED);
  localStorage.removeItem(STORAGE_KEYS.USER_TYPE);
  localStorage.removeItem(STORAGE_KEYS.ORGANIZATION_ID);
  localStorage.removeItem(STORAGE_KEYS.ROLE);
  localStorage.removeItem(STORAGE_KEYS.MEMBER_ID);
  // Note: httpOnly cookies cannot be cleared from JS.
  // Logout should call a backend endpoint that clears cookies via Set-Cookie with Max-Age=0.
};
